/**
 * The get-started funnel's "I am a…" vocabulary and selection rules.
 *
 * Multi-select, but only some combos make sense: a visitor can be any single
 * role, or a Parent who is also a Teacher or an Administrator. Students and
 * "Other" are exclusive, and Teacher/Administrator can't be combined with
 * each other. The server stores the selection comma-joined on the interest
 * lead (e.g. "PARENT,TEACHER").
 */

export const GET_STARTED_ROLE_OPTIONS = [
  { value: 'PARENT', label: 'Parent' },
  { value: 'TEACHER', label: 'Teacher' },
  { value: 'ADMINISTRATOR', label: 'Administrator' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'OTHER', label: 'Other' },
] as const

/**
 * Toggle `picked` within the current selection, enforcing the allowed
 * combos: any single role, PARENT+TEACHER, or PARENT+ADMINISTRATOR.
 * Conflicting picks displace rather than no-op (picking STUDENT clears
 * everything else; picking TEACHER drops ADMINISTRATOR and vice versa),
 * so a card tap always does something visible.
 */
export function toggleRole(current: string[], picked: string): string[] {
  if (current.includes(picked)) {
    return current.filter((r) => r !== picked)
  }
  if (picked === 'STUDENT' || picked === 'OTHER') {
    return [picked]
  }
  if (picked === 'PARENT') {
    // Parent joins an existing Teacher/Administrator pick; anything else clears.
    return [...current.filter((r) => r === 'TEACHER' || r === 'ADMINISTRATOR'), 'PARENT']
  }
  // TEACHER or ADMINISTRATOR — keeps a Parent pick, displaces the other one.
  return [...current.filter((r) => r === 'PARENT'), picked]
}

/**
 * The noun phrase a get-started visitor uses to describe themselves when
 * nudging their school — the "___" in "I'm a ___ at <school>". Roles render in
 * a fixed order (parent, teacher, student) regardless of pick order, so
 * PARENT+TEACHER always reads "parent and teacher". Returns null when there is
 * no concrete school role to claim (OTHER, ADMINISTRATOR, or an empty /
 * unrecognized set) so callers can fall back to role-free phrasing rather than
 * putting a word in the sender's mouth.
 */
export function senderRoleNoun(roles: string[]): string | null {
  const ORDERED: ReadonlyArray<readonly [string, string]> = [
    ['PARENT', 'parent'],
    ['TEACHER', 'teacher'],
    ['STUDENT', 'student'],
  ]
  const nouns = ORDERED.filter(([code]) => roles.includes(code)).map(([, noun]) => noun)
  if (nouns.length === 0) {
    return null
  }
  if (nouns.length === 1) {
    return nouns[0]
  }
  return `${nouns.slice(0, -1).join(', ')} and ${nouns[nouns.length - 1]}`
}
